/**
 * Nutzerverwaltung. Routen aus Phase 1 BEKANNT UND STABIL
 * (`backend/src/http/routes/users.ts`): `GET /api/users` liefert ein blankes Array,
 * `POST /api/users` und `PUT /api/users/:id` je ein blankes Objekt.
 *
 * ===================== Befund 14 (Task 4, Entscheidungsvorlage) =====================
 *
 * `PUT /api/users/:id` ERHOEHT `row_version`, PRUEFT SIE ABER NICHT
 * (`users.ts:251`: `SET ... row_version = row_version + 1 ... WHERE id = $4` - kein
 * `AND row_version = $n`). Es gibt hier deshalb keinen 409 und folglich auch KEINEN
 * Konflikt-Banner in der Nutzerverwaltung (Task 26). Zwei gleichzeitige Bearbeiter ueberschreiben
 * sich gegenseitig lautlos - der letzte gewinnt.
 *
 * `nutzerAendern` sendet aus genau diesem Grund KEINE `rowVersion` und benutzt deshalb auch NICHT
 * `ressource()` aus `basis.ts`: eine mitgesendete, nirgends gepruefte Version waere eine
 * Behauptung von Nebenlaeufigkeitsschutz, den es hier nicht gibt.
 *
 * Wird die Pruefung nachgezogen, aendert sich AUSSCHLIESSLICH diese Datei: `nutzerAendern` bekommt
 * einen `rowVersion`-Parameter (oder wird auf `ressource("/api/users")` umgestellt), und Task 26
 * zeigt denselben Banner wie die uebrigen Flaechen - eine Ergaenzung, kein Umbau.
 */
import { anfrage } from "./basis.ts";
import type { Nutzer } from "../state/reduzierer.ts";

export interface NutzerAnlegenDaten {
  username: string;
  displayName?: string;
  role: string;
  password: string;
  /**
   * Pflicht bei jedem schreibenden Kontotyp, OPTIONAL bei `read_only` ("Nur lesen": das Konto schreibt nie nach
   * JTL). Ein Konto "Nur lesen" ohne Login sendet das Feld GAR NICHT - nie `null` (der Server lehnt `null` mit 400
   * ab), und `""` ist bei `read_only` zwar erlaubt (kein Login), aber ueberfluessig. Ein angegebener Login wird bei
   * jedem Kontotyp gegen JTL aufgeloest. Das Formular (`Users/NutzerFormular.tsx`) fuehrt dieselbe Form.
   */
  jtlLogin?: string;
}

export interface NutzerAendernDaten {
  displayName?: string;
  role?: string;
  active?: boolean;
  /**
   * `resetUserPassword(id, username)` (index.html:6317-6321) schickt genau dieses Feld im PUT-Koerper.
   *
   * SEIT DEM 2026-08-11 VERARBEITET DER SERVER DAS FELD WIEDER. Zuvor las `PUT /api/users/:id`
   * (`backend/src/http/routes/users.ts`) aus dem Koerper ausschliesslich `displayName`/`role`/
   * `active` und ignorierte `password` kommentarlos - das Setzen eines neuen Passworts hatte
   * serverseitig KEINE Wirkung.
   *
   * DAS WAR EINE PORTIERUNGSREGRESSION, keine nie dagewesene Luecke: der Bestand-Worker
   * (`cloudflare/src/worker.ts`, PUT-Zweig, such nach `body?.password`) verarbeitete das Feld sehr
   * wohl - Mindestlaenge acht, neues Salt, `passwordHash`. Der Express-Port hatte den Zweig beim
   * Portieren stillschweigend fallen gelassen, und KEIN Backend-Test deckte ihn ab: die
   * Integrationstests kannten `password` nur beim Anlegen. Genau daran ist es durchgerutscht - ein
   * Test, der nur den 200 prueft, haette die Regression nie gefunden.
   *
   * Der Endpunkt prueft den Typ inzwischen strenger als der Bestand: eine Nicht-Zeichenkette wird
   * mit 400 abgelehnt, statt ueber `String()` zu einem festen Passwort zu werden (`{"password": {}}`
   * ergab "[object Object]", fuenfzehn Zeichen, also lang genug). Das ist eine bewusste, dort
   * begruendete Abweichung. Die LEERE Zeichenkette bleibt woertlich: sie setzt nicht und wird nicht
   * abgelehnt.
   *
   * Der Aufruf hier war nie veraendert und bleibt woertlich wie der Bestand: `resetUserPassword`
   * sendet genau `{ password }`.
   */
  password?: string;
  /**
   * Auftrag "JTL-Login in der Nutzerverwaltung" (2026-09-07): das neue Eingabefeld neben dem
   * Anzeigenamen (`NutzerSeite.tsx`). `undefined` = unveraendert (der gespeicherte Login bleibt; so
   * funktionieren Teilaenderungen wie Kontotyp, Name, Aktiv-Status und Passwort), ein nicht-leerer Wert
   * wird serverseitig GEGEN DIE ECHTE JTL-INSTANZ geprueft (`http/routes/users.ts`, `nummerAusJtl`) -
   * ein Tippfehler kommt deshalb als 400 zurueck, nicht erst beim naechsten JTL-Schreibversuch des
   * betroffenen Nutzers (`JtlBenutzerFehltError`, `jtlSchreiben.ts`).
   *
   * `""` (nach Trim) loescht den hinterlegten Login samt der aufgeloesten `jtl_kbenutzer` - aber NUR, wenn
   * die Rolle danach `read_only` ist ("Nur lesen" braucht keinen Login); bei jedem schreibenden Kontotyp ist
   * es 400. `null` oder ein anderer Nicht-String ist immer 400 und wird nie gesendet.
   */
  jtlLogin?: string;
}

export function nutzerListe(signal?: AbortSignal): Promise<Nutzer[]> {
  return anfrage<Nutzer[]>("/api/users", { signal });
}

/** Aktive JTL-Logins fuer die verpflichtende Auswahl in der Nutzerverwaltung. */
export async function jtlLoginsLaden(signal?: AbortSignal): Promise<string[]> {
  const antwort = await anfrage<{ jtlLogins: string[] }>("/api/users/jtl-logins", { signal });
  return antwort.jtlLogins;
}

export function nutzerAnlegen(daten: NutzerAnlegenDaten): Promise<Nutzer> {
  return anfrage<Nutzer>("/api/users", { methode: "POST", koerper: daten });
}

/** Siehe Befund 14 im Kopfkommentar: bewusst ohne `rowVersion`. */
export function nutzerAendern(id: string, daten: NutzerAendernDaten): Promise<Nutzer> {
  return anfrage<Nutzer>(`/api/users/${encodeURIComponent(id)}`, {
    methode: "PUT",
    koerper: daten,
  });
}
